/**
 * Turns the backend's error code into something a person can act on.
 *
 * A failed turn is persisted as an error message whose text IS the code, and the
 * chat used to render that code verbatim — users were shown `openai_error` or
 * `unexpected_error` and left to guess whether to rephrase, wait, or report it.
 *
 * Unknown codes fall back to a generic sentence rather than being hidden: a code
 * this map has not caught up with is still a real failure the user must be told
 * about. The raw code stays visible alongside the sentence so support can act on a
 * screenshot.
 */

const ERROR_MESSAGE_KEYS: Record<string, string> = {
  // The user can fix these themselves.
  openai_prompt_flagged: 'error_prompt_flagged',
  openai_content_filter: 'error_content_filter',
  openai_input_string_too_long: 'error_input_too_long',
  openai_context_window_exceeded: 'error_context_window',
  // The question needed more steps than one answer may take (AB#6507). The
  // backend lands most such turns on an answer; this is what is left over.
  agent_step_limit: 'error_step_limit',

  // Temporary — worth waiting and retrying.
  openai_rate_limit_exceeded: 'error_rate_limited',
  openai_too_many_requests: 'error_rate_limited',
  openai_resource_exhausted: 'error_capacity',
  mcp_unavailable: 'error_tools_unavailable',
  agent_timeout: 'error_timeout',
  agent_did_not_finish: 'error_incomplete',
  // The worker running the turn died; the next run marks its messages
  // interrupted and says so (AB#6760).
  run_interrupted: 'error_run_interrupted',
}

export const errorMessageKey = (code: string | undefined): string =>
  ERROR_MESSAGE_KEYS[(code ?? '').trim()] ?? 'error_unknown'
